/**
 * VistoeraTASK-00555-20261011-2024-001 — Image Asset Center entry for the
 * existing Canvas image editor (`CanvasImageEditorOverlay` →
 * `DualPaneCutoutEditor`). No second editor; no Canvas node on save.
 */
import { lazy, Suspense, useCallback, useMemo, useState } from 'react'
import { message } from 'antd'
import type { LibraryAssetItem } from '../../api/client'
import { resolveImagePickerSelection } from '../api'
import ImageEditorErrorBoundary from './contentProduction/canvas/nodes/ImageEditorErrorBoundary'
import {
  createEditorDiagnosticsSnapshot,
  type EditorDiagnosticsRef,
} from '../../components/editor/editorDiagnostics'
import { useTranslation } from 'react-i18next'

const CanvasImageEditorOverlay = lazy(
  () => import('./contentProduction/canvas/nodes/CanvasImageEditorOverlay'),
)

export type ImageAssetEditorTarget = {
  item: LibraryAssetItem
  siteId: string
  sourceAssetId: string
  sourceLabel: string
  knownWidth: number | null
  knownHeight: number | null
  knownSizeBytes: number | null
}

type Props = {
  workspaceId: string
  /** Current folder filter — new saves land here when set (team collections). */
  collectionId?: string | null
  onSaved: () => void | Promise<void>
}

function pickerItemId(item: LibraryAssetItem): string {
  if (item.picker_item_id) return item.picker_item_id
  if (item.id.startsWith('sa:') || item.id.startsWith('lib:')) return item.id
  return `lib:${item.id}`
}

function siteAssetIdFromItem(item: LibraryAssetItem): string | null {
  const pid = pickerItemId(item)
  if (pid.startsWith('sa:')) return pid.slice(3)
  if (item.reference_type === 'site_asset' && item.id && !item.id.includes(':')) return item.id
  return null
}

export function useImageAssetCanvasEditor({ workspaceId, collectionId, onSaved }: Props) {
  const { t } = useTranslation('imageAssets')
  const [target, setTarget] = useState<ImageAssetEditorTarget | null>(null)
  const [opening, setOpening] = useState(false)
  const [editorKey, setEditorKey] = useState(0)
  const diagnosticsRef: EditorDiagnosticsRef = useMemo(
    () => ({ current: createEditorDiagnosticsSnapshot() }),
    [editorKey],
  )

  const close = useCallback(() => setTarget(null), [])

  const openForItem = useCallback(
    async (item: LibraryAssetItem, siteId: string | null | undefined) => {
      if (!siteId) {
        message.error(t('imageEditorEntry.noSite'))
        return
      }
      if (opening || target) return
      setOpening(true)
      const hide = message.loading({ content: t('imageEditorEntry.opening'), duration: 0 })
      try {
        let sourceAssetId = siteAssetIdFromItem(item)
        if (!sourceAssetId) {
          const { data } = await resolveImagePickerSelection(workspaceId, {
            picker_item_id: pickerItemId(item),
            target: 'site_asset',
            site_id: siteId,
          })
          sourceAssetId = data.asset_id
        }
        setTarget({
          item,
          siteId,
          sourceAssetId,
          sourceLabel: item.filename,
          knownWidth: item.width,
          knownHeight: item.height,
          knownSizeBytes: item.size_bytes ?? null,
        })
        setEditorKey((k) => k + 1)
      } catch {
        message.error(t('imageEditorEntry.openFailed'))
      } finally {
        hide()
        setOpening(false)
      }
    },
    [opening, target, t, workspaceId],
  )

  const layer =
    target ? (
      <Suspense fallback={null}>
        <ImageEditorErrorBoundary
          key={editorKey}
          onClose={close}
          onRetry={() => setEditorKey((k) => k + 1)}
          diagnosticsRef={diagnosticsRef}
        >
          <CanvasImageEditorOverlay
            workspaceId={workspaceId}
            siteId={target.siteId}
            sourceAssetId={target.sourceAssetId}
            sourceLabel={target.sourceLabel}
            knownSourceWidth={target.knownWidth}
            knownSourceHeight={target.knownHeight}
            knownSourceSizeBytes={target.knownSizeBytes}
            entrySurface="image_assets"
            requireCanvasNodeSync={false}
            uploadCollectionId={collectionId ?? target.item.collection_id ?? null}
            onClose={close}
            onSavedAsNewAsset={async () => {
              await onSaved()
              return true
            }}
            diagnosticsRef={diagnosticsRef}
          />
        </ImageEditorErrorBoundary>
      </Suspense>
    ) : null

  return { openForItem, editorLayer: layer, editorOpen: Boolean(target) }
}
